import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import PropertyFormHeader from 'MeetingsLib/components/header/PropertyFormHeader';
import BookingFormFields from 'MeetingsLib/components/input/BookingFormFields';
import StepButtonsFooter from 'MeetingsLib/components/page/StepButtonsFooter';
import LegalConsentForm from 'MeetingsLib/components/sections/LegalConsentForm';
import RecaptchaField from 'MeetingsLib/components/input/RecaptchaField';
import AddGuestsForm from './AddGuestsForm';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import { canSubmitStagedPropertyForm as canSubmitStagedPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { getStagedFormFieldValidation } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import { getStagedLegalConsentResponses, getAllStagedRequiredConsentCollected } from 'MeetingsLib/selectors/LegalConsentFormSelectors';
import { getCustomParams as getCustomParamsSelector, getEvent as getEventSelector, getOffline as getOfflineSelector, getIsLegitimateInterest, getPaidMeetingsAvailableSelector, getRecaptchaEnabled } from 'MeetingsLib/selectors/BookInfoSelectors';
import { resetConsentResponses, resetFormState, setFormState, setFormValue, submitConsentResponses } from 'MeetingsLib/redux/actions/FormActions';
import { getCanAddGuests } from 'MeetingsLib/selectors/BookInfoSelectors';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ButtonWrapper = styled.div.withConfig({
  displayName: "PropertyFormV2__ButtonWrapper"
})(["display:flex;flex-direction:column;gap:inherit;align-items:inherit;justify-content:inherit;"]);
const FORM_TOOLTIP_MESSAGES = {
  INCOMPLETE: 'buttons.tooltip.formIncompleteOrHasErrors',
  NO_CONSENT: 'buttons.tooltip.missingConsent'
};
const PropertyForm = ({
  branding,
  compact,
  embedded,
  canSubmitInitialPropertyForm,
  submittingMeeting,
  goToPreviousStep
}) => {
  const dispatch = useDispatch();
  const canSubmitStagedPropertyForm = useSelector(canSubmitStagedPropertyFormSelector);
  const dateTime = useSelector(getEventSelector).dateTime;
  const location = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getCustomParamsSelector).location;
  const formFields = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getCustomParamsSelector).formFields;
  const stagedFormState = useSelector(state => state.stagedFormState);
  const formStateValidation = useSelector(getStagedFormFieldValidation);
  /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  const recaptchaEnabled = useSelector(getRecaptchaEnabled);
  const legalConsentEnabled = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getCustomParamsSelector).legalConsentEnabled;
  /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  const offline = useSelector(getOfflineSelector);
  const userTimezone = useSelector(state => state.userTimezone);
  const isRescheduling = useSelector(state => state.isRescheduling);
  const stagedLegalConsentResponses = useSelector(getStagedLegalConsentResponses);
  const allRequiredConsentCollected = useSelector(getAllStagedRequiredConsentCollected);
  /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  const isLegitimateInterest = useSelector(getIsLegitimateInterest);
  const isPaidMeeting = useSelector(getPaidMeetingsAvailableSelector);
  const canAddGuests = useSelector(getCanAddGuests);
  const showButtons = !canSubmitInitialPropertyForm || recaptchaEnabled || legalConsentEnabled || isPaidMeeting;
  const buttonsRef = useRef(null);
  const [buttonsHeight, setButtonsHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const handleGoToPreviousStep = useCallback(() => {
    dispatch(resetFormState());
    dispatch(resetConsentResponses());
    goToPreviousStep();
  }, [dispatch, goToPreviousStep]);
  const handleGoToNextStep = useCallback(() => {
    void dispatch(setFormState(stagedFormState));
    void dispatch(submitConsentResponses(stagedLegalConsentResponses));
  }, [dispatch, stagedFormState, stagedLegalConsentResponses]);
  const handleFormValueUpdate = useCallback((fieldName, value) => {
    dispatch(setFormValue(fieldName, value));
  }, [dispatch]);
  const getTooltipMessage = () => {
    if (!isRescheduling) {
      if (!canSubmitStagedPropertyForm) {
        return FORM_TOOLTIP_MESSAGES.INCOMPLETE;
      }
      if (legalConsentEnabled && !(allRequiredConsentCollected || isLegitimateInterest)) {
        return FORM_TOOLTIP_MESSAGES.NO_CONSENT;
      }
    }
    return null;
  };
  const renderFormFields = () => {
    return /*#__PURE__*/_jsx(TFlex, {
      direction: "column",
      align: "stretch",
      children: /*#__PURE__*/_jsx(BookingFormFields, {
        formFields: formFields,
        formState: stagedFormState,
        formStateValidation: formStateValidation,
        setFormValue: handleFormValueUpdate,
        disabled: isRescheduling
      })
    }, "fields");
  };
  const renderFormHeader = () => {
    return /*#__PURE__*/_jsx(PropertyFormHeader, {
      userTimezone: userTimezone,
      dateTime: dateTime,
      location: location,
      handleGoToPreviousStep: handleGoToPreviousStep
    });
  };
  const renderedAddGuests = useMemo(() => {
    return /*#__PURE__*/_jsx(AddGuestsForm, {
      isRescheduling: isRescheduling
    });
  }, [isRescheduling]);
  const renderLegalConsent = () => {
    return /*#__PURE__*/_jsx(LegalConsentForm, {
      compact: compact,
      branding: branding,
      submittingMeeting: submittingMeeting,
      dateTime: dateTime
    }, `${compact ? 'compact' : 'full'}-legalConsent`);
  };
  const renderRecaptcha = () => {
    if (recaptchaEnabled && !isPaidMeeting) {
      return /*#__PURE__*/_jsx(View, {
        marginInline: compact ? 'auto' : undefined,
        paddingInline: !compact ? '800' : undefined,
        children: /*#__PURE__*/_jsx(RecaptchaField, {})
      });
    }
    return null;
  };
  const renderFormButtons = () => {
    const tooltipMessage = getTooltipMessage();
    return /*#__PURE__*/_jsx(StepButtonsFooter, {
      branding: branding,
      compact: compact,
      submittingMeeting: submittingMeeting,
      canProceed: tooltipMessage === null,
      tooltipTitle: tooltipMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: tooltipMessage
      }) : null,
      goToPreviousStep: handleGoToPreviousStep,
      goToNextStep: handleGoToNextStep,
      isLastStep: !isPaidMeeting,
      offline: offline
    }, "buttons");
  };
  useEffect(() => {
    if (!embedded) {
      return undefined;
    }
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [embedded]);
  useEffect(() => {
    const buttonsEl = buttonsRef.current;
    if (!buttonsEl || !compact || !embedded || !showButtons) {
      setButtonsHeight(0);
      return undefined;
    }
    const buttonsResize = rect => {
      setButtonsHeight(rect.height);
    };
    const observer = new ResizeObserver(entries => buttonsResize(entries[0].contentRect));
    buttonsResize(buttonsEl.getBoundingClientRect());
    observer.observe(buttonsEl);
    return () => observer.disconnect();
  }, [embedded, compact, showButtons]);
  const sectionHeight = embedded && compact ? showButtons ? `${viewportHeight - buttonsHeight}px` : `${viewportHeight}px` : undefined;
  return /*#__PURE__*/_jsxs(CardWrapper, {
    flexDirection: "column",
    minBlockSize: "0",
    "data-selenium-test": "booking-form-step",
    children: [/*#__PURE__*/_jsxs(CardSection, {
      flexDirection: "column",
      minBlockSize: "0",
      padding: "0",
      blockSize: sectionHeight,
      scrollable: true,
      "data-test-id": "property-form-scroll-section",
      children: [renderFormHeader(), canSubmitInitialPropertyForm && !recaptchaEnabled && !legalConsentEnabled && !isPaidMeeting ? /*#__PURE__*/_jsx(LoadingSpinner, {
        "data-test-id": "property-form-loading-spinner",
        size: "medium",
        grow: true
      }) : renderFormFields(), canAddGuests && renderedAddGuests, !isRescheduling && legalConsentEnabled && renderLegalConsent(), renderRecaptcha()]
    }), showButtons && /*#__PURE__*/_jsx(ButtonWrapper, {
      ref: buttonsRef,
      children: renderFormButtons()
    })]
  });
};
export default PropertyForm;